import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { themeNames } from '@upgrade/themes';
import { mdiFacebook, mdiInstagram, mdiLinkedin } from '@mdi/js';
import Text from '../Typography/Text';
import Link from '../Link';
import Row from '../Row';
import Column from '../Column';
import FooterBadges from './badges';
import getCopyrightYear from './copyright-year';
import FooterLegal from './legal';
import FooterInfo from './info';
import FooterLogo from './logo';
import FooterLinks from './links';
import FooterSocial from './social';
import FooterSocialItem from './social-item';
import { FooterLinksItem, FooterLinksTextItem } from './links-items';
import xLogo from './x-logo.png';
import * as Styled from './styles';
import { CREDIT_HEALTH, FACEBOOK, HELP_CENTER, INSTAGRAM, LINKEDIN, PRIVACY, TERMS, X, UNIVERSAL_CREDIT_HELP_CENTER, FIND_ATMS_LOGGED_OUT } from '../constants/paths';
export const FOOTER_LINKS = {
  CREDIT_HEALTH: /*#__PURE__*/React.createElement(FooterLinksItem, {
    key: "creditHealth",
    href: CREDIT_HEALTH,
    target: "_blank",
    linkProps: {
      rel: 'noreferrer noopener'
    }
  }, "Credit Health"),
  HELP_CENTER: /*#__PURE__*/React.createElement(FooterLinksItem, {
    key: "help-center",
    href: HELP_CENTER,
    target: "_blank",
    linkProps: {
      rel: 'noreferrer noopener'
    }
  }, "Help Center"),
  UNIVERSAL_CREDIT_HELP_CENTER: /*#__PURE__*/React.createElement(FooterLinksItem, {
    key: "universal-credit-help-center",
    href: UNIVERSAL_CREDIT_HELP_CENTER,
    target: "_blank",
    linkProps: {
      rel: 'noreferrer noopener'
    }
  }, "Help Center"),
  PRIVACY: /*#__PURE__*/React.createElement(FooterLinksItem, {
    key: "privacy",
    href: PRIVACY,
    target: "_blank",
    linkProps: {
      rel: 'noreferrer noopener'
    }
  }, "Privacy"),
  TERMS: /*#__PURE__*/React.createElement(FooterLinksItem, {
    key: "terms",
    href: TERMS,
    target: "_blank",
    linkProps: {
      rel: 'noreferrer noopener'
    }
  }, "Terms of Use"),
  FIND_ATMS_LOGGED_OUT: /*#__PURE__*/React.createElement(FooterLinksItem, {
    key: "find_atms_logged_out",
    href: FIND_ATMS_LOGGED_OUT,
    target: "_blank",
    linkProps: {
      rel: 'noreferrer noopener'
    }
  }, "Find ATMs")
};
export const FOOTER_ADDRESSES = {
  SAN_FRANCISCO: /*#__PURE__*/React.createElement(React.Fragment, null, "275 Battery Street, 23rd Floor", /*#__PURE__*/React.createElement("br", {
    "aria-hidden": "true"
  }), "San Francisco, CA 94111, USA"),
  TORONTO: /*#__PURE__*/React.createElement(React.Fragment, null, "Flex Pay \u2105 Uplift Canada Services ULC", /*#__PURE__*/React.createElement("br", {
    "aria-hidden": "true"
  }), "410 Adelaide Street West, Suite 220", /*#__PURE__*/React.createElement("br", {
    "aria-hidden": "true"
  }), "Toronto, ON, M5V 1S8")
};
const defaultLinks = [FOOTER_LINKS.CREDIT_HEALTH, FOOTER_LINKS.HELP_CENTER, FOOTER_LINKS.PRIVACY, FOOTER_LINKS.TERMS];
const defaultContact = /*#__PURE__*/React.createElement(Text, {
  as: "p",
  size: "sm"
}, /*#__PURE__*/React.createElement(Styled.ContactEmail, null, "contact@", /*#__PURE__*/React.createElement(Text, {
  as: "span",
  className: "hidden"
}, "null"), "upgrade.com", /*#__PURE__*/React.createElement(Text, {
  as: "span",
  className: "hidden"
}, "null")));
const defaultSocialLinks = [/*#__PURE__*/React.createElement(FooterSocialItem, {
  key: "facebook",
  href: FACEBOOK,
  icon: mdiFacebook,
  ariaTitle: "Facebook"
}), /*#__PURE__*/React.createElement(FooterSocialItem, {
  key: "instagram",
  href: INSTAGRAM,
  icon: mdiInstagram,
  ariaTitle: "Instagram"
}), /*#__PURE__*/React.createElement(FooterSocialItem, {
  key: "x",
  href: X,
  imgSrc: xLogo,
  ariaTitle: "X"
}), /*#__PURE__*/React.createElement(FooterSocialItem, {
  key: "linkedin",
  href: LINKEDIN,
  icon: mdiLinkedin,
  ariaTitle: "LinkedIn"
})];
export default function Footer({
  links = defaultLinks,
  logo = /*#__PURE__*/React.createElement(FooterLogo, {
    href: "/"
  }),
  contact = defaultContact,
  legal,
  social = defaultSocialLinks,
  notice,
  address = FOOTER_ADDRESSES.SAN_FRANCISCO,
  company,
  showBadges = true,
  showPaymentAddress = true,
  copyrightMessage: _copyrightMessage,
  allRightsReservedMessage = 'All rights reserved.'
}) {
  const theme = useTheme();
  const isUniversal = theme.name === themeNames.UNIVERSAL_CREDIT_THEME_NAME;
  const {
    betterBusinessBureauBadge,
    trustedSite
  } = theme;
  const companyName = company || (isUniversal ? 'Universal Credit, Inc.' : 'Upgrade, Inc.');
  const showPaymentAddressMessage = showPaymentAddress && !isUniversal;
  const copyrightMessage = _copyrightMessage || /*#__PURE__*/React.createElement(React.Fragment, null, "Copyright ", getCopyrightYear(), " ", companyName);
  return /*#__PURE__*/React.createElement(Styled.Footer, {
    className: "footer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "container-fluid layout-default"
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Column, {
    xs: 12,
    sm: 12,
    md: 9,
    smHorizontalAlign: "start",
    xsHorizontalAlign: "center"
  }, /*#__PURE__*/React.createElement(FooterLinks, null, links)), /*#__PURE__*/React.createElement(Column, {
    xs: 12,
    sm: 12,
    md: 3,
    xsHorizontalAlign: "center",
    mdHorizontalAlign: "end"
  }, isUniversal ? /*#__PURE__*/React.createElement(FooterLogo, null) : logo, isUniversal && /*#__PURE__*/React.createElement(Text, {
    as: "p",
    size: "sm"
  }, "Provided by", ' ', /*#__PURE__*/React.createElement(Link, {
    type: "text",
    inherit: true,
    href: "https://www.upgrade.com"
  }, "Upgrade, Inc.")), /*#__PURE__*/React.createElement(Text, {
    as: "p",
    size: "sm"
  }, address), showPaymentAddressMessage && /*#__PURE__*/React.createElement(Text, {
    as: "p",
    italic: true,
    size: "sm"
  }, "Not for payments. Payment ", /*#__PURE__*/React.createElement("br", {
    "aria-hidden": "true"
  }), "address can be found", ' ', /*#__PURE__*/React.createElement(Link, {
    type: "text",
    inherit: true,
    href: "https://upgrade.zendesk.com/hc/en-us/articles/115000326587-Can-I-pay-by-check-here",
    "aria-label": "More about payment address information"
  }, "here"), "."), !isUniversal && /*#__PURE__*/React.createElement(React.Fragment, null, contact, /*#__PURE__*/React.createElement(FooterSocial, null, social)))), /*#__PURE__*/React.createElement(Row, {
    className: "footer-info"
  }, /*#__PURE__*/React.createElement(Column, {
    xs: 12,
    xsHorizontalAlign: "center",
    smHorizontalAlign: "start"
  }, legal && /*#__PURE__*/React.createElement(FooterLegal, null, legal), /*#__PURE__*/React.createElement(FooterInfo, {
    key: "copyright"
  }, copyrightMessage, /*#__PURE__*/React.createElement("br", null), isUniversal ? /*#__PURE__*/React.createElement(Text, {
    as: "span"
  }, "All rights reserved. Website owned and operated by Upgrade, Inc. Upgrade also does business as Universal Credit and under the names", ' ', /*#__PURE__*/React.createElement(Link, {
    type: "text",
    inherit: true,
    href: "https://www.upgrade.com/landing/dba"
  }, "here"), ' ', "(together, \"Upgrade\" or \"Universal Credit\").") : /*#__PURE__*/React.createElement(Text, {
    as: "span"
  }, allRightsReservedMessage)), notice && /*#__PURE__*/React.createElement(Text, {
    as: "div",
    key: "bank-notice",
    size: "sm"
  }, notice), showBadges && /*#__PURE__*/React.createElement(FooterBadges, {
    key: "badges",
    className: "footer-badges"
  }, betterBusinessBureauBadge && /*#__PURE__*/React.createElement(Text, {
    as: "span",
    className: "footer-badges__item"
  }, /*#__PURE__*/React.createElement(Link, {
    target: "_blank",
    rel: "noreferrer noopener",
    href: betterBusinessBureauBadge.link,
    className: "footer-badges__item"
  }, /*#__PURE__*/React.createElement("img", {
    src: betterBusinessBureauBadge.imageSrc,
    alt: "Better Business Bureau Accredited A+ Rating as of January Twenty First 2025, check out our profile",
    width: "137",
    height: "39"
  }))), /*#__PURE__*/React.createElement(Text, {
    as: "span",
    className: "footer-badges__item"
  }, /*#__PURE__*/React.createElement(Link, {
    target: "_blank",
    rel: "noreferrer noopener",
    href: "https://www.occ.gov/publications-and-resources/publications/consumer-protection-pubs/files/equal-housing-lender.html"
  }, /*#__PURE__*/React.createElement("img", {
    alt: "Equal Housing Lender",
    src: "https://www.upgrade.com/assets/images/equal-housing.svg"
  }))), trustedSite && /*#__PURE__*/React.createElement(Link, {
    target: "_blank",
    rel: "noreferrer noopener",
    href: trustedSite.link
  }, /*#__PURE__*/React.createElement("img", {
    border: "0",
    height: "39",
    width: "95",
    src: trustedSite.imageSrc,
    alt: "Trusted Site Certified Secure"
  })))))));
}
Footer.propTypes = {
  links: PropTypes.arrayOf(PropTypes.element),
  contact: PropTypes.node,
  logo: PropTypes.element,
  social: PropTypes.arrayOf(PropTypes.element),
  legal: PropTypes.node,
  notice: PropTypes.node,
  address: PropTypes.node,
  company: PropTypes.string,
  showBadges: PropTypes.bool,
  showPaymentAddress: PropTypes.bool
};
Object.assign(Footer, {
  LinksItem: FooterLinksItem,
  LinksTextItem: FooterLinksTextItem,
  SocialItem: FooterSocialItem,
  Logo: FooterLogo
});
export { FooterLinksItem, FooterLinksTextItem, FooterLogo, FooterSocialItem };
//# sourceMappingURL=index.js.map